import { Box, IconButton, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  Control,
  FieldErrors,
  UseFormResetField,
  useWatch,
} from 'react-hook-form';

import { GroupedSelectFormField } from '@/components/Form/GroupedSelect';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';

import { useTranslate } from '@/hooks/useTranslate';

import { NoTaxResidencyReasonCode } from '@/types/api/tax-info';
import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';

import { OtherCountriesTaxFormValues } from '../utils/types';
import { FOREIGN_NON_TAX_RESIDENCY_REASON_OTHER_MAX_LENGTH } from './constants';
import { getForeignNonTaxResidencyReasonGroupedSelectConfig } from './utils';

import {
  TaxInfoFieldLabel,
  TaxInfoQuestionSection,
  TaxInfoResidenceBlock,
} from '../TaxInfoDrawer.styled';

type ForeignNonTaxResidencyBlockProps = {
  index: number;
  total: number;
  control: Control<OtherCountriesTaxFormValues>;
  errors: FieldErrors<OtherCountriesTaxFormValues>;
  countryOptions: Option[];
  isPending: boolean;
  resetField: UseFormResetField<OtherCountriesTaxFormValues>;
  onDelete: () => void;
};

export function ForeignNonTaxResidencyBlock({
  index,
  total,
  control,
  errors,
  countryOptions,
  isPending,
  resetField,
  onDelete,
}: Readonly<ForeignNonTaxResidencyBlockProps>) {
  const { t } = useTranslate();
  const blockErrors = errors.foreignNonTaxResidencies?.[index];

  const { groups, otherOption } = useMemo(
    () => getForeignNonTaxResidencyReasonGroupedSelectConfig(t),
    [t],
  );

  const selectedReason = useWatch({
    control,
    name: `foreignNonTaxResidencies.${index}.noTaxResidencyReasonCode`,
  });
  const otherReason = useWatch({
    control,
    name: `foreignNonTaxResidencies.${index}.noTaxResidencyReasonOther`,
  });

  const showOtherReasonField =
    selectedReason === NoTaxResidencyReasonCode.OTHER;
  const otherReasonCharactersLeft =
    FOREIGN_NON_TAX_RESIDENCY_REASON_OTHER_MAX_LENGTH -
    (otherReason?.length ?? 0);

  return (
    <TaxInfoQuestionSection>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="body1Medium">
          {t('taxInfoDrawer.question2.foreignNonTaxResidency.blockTitle')}
        </Typography>
        {total > 1 && (
          <IconButton
            onClick={onDelete}
            disabled={isPending}
            sx={{ width: 24, height: 24 }}
            title={t(
              'taxInfoDrawer.question2.foreignNonTaxResidency.deleteBlock',
            )}
            {...getDataTestId(
              `tax-info-foreign-non-tax-residency-delete-${index}`,
            )}
          >
            <Icon name="delete" size={16} />
          </IconButton>
        )}
      </Box>

      <TaxInfoResidenceBlock>
        <Select
          control={control}
          name={`foreignNonTaxResidencies.${index}.countryCode`}
          options={countryOptions}
          placeholder={t(
            'taxInfoDrawer.question2.foreignNonTaxResidency.country.placeholder',
          )}
          error={!!blockErrors?.countryCode}
          helperText={blockErrors?.countryCode?.message}
          fullWidth
          disabled={isPending}
          useDrawerOnMobile
          withSearchBar
          {...getDataTestId(
            `tax-info-foreign-non-tax-residency-country-${index}`,
          )}
        />

        <Box display="flex" flexDirection="column" gap={1}>
          <TaxInfoFieldLabel variant="caption">
            {t('taxInfoDrawer.question2.foreignNonTaxResidency.reason.label')}
          </TaxInfoFieldLabel>

          <GroupedSelectFormField
            control={control}
            name={`foreignNonTaxResidencies.${index}.noTaxResidencyReasonCode`}
            selectionMode="single"
            title={t(
              'taxInfoDrawer.question2.foreignNonTaxResidency.reason.drawerTitle',
            )}
            placeholder={t(
              'taxInfoDrawer.question2.foreignNonTaxResidency.reason.placeholder',
            )}
            groups={groups}
            otherOption={otherOption}
            disabled={isPending}
            error={!!blockErrors?.noTaxResidencyReasonCode}
            helperText={blockErrors?.noTaxResidencyReasonCode?.message}
            onSelectChange={selectedValue => {
              if (selectedValue !== NoTaxResidencyReasonCode.OTHER) {
                resetField(
                  `foreignNonTaxResidencies.${index}.noTaxResidencyReasonOther`,
                  { defaultValue: '' },
                );
              }
            }}
            {...getDataTestId(
              `tax-info-foreign-non-tax-residency-reason-${index}`,
            )}
          />
        </Box>

        {showOtherReasonField && (
          <TextInput
            control={control}
            name={`foreignNonTaxResidencies.${index}.noTaxResidencyReasonOther`}
            label={t(
              'taxInfoDrawer.question2.foreignNonTaxResidency.reasonOther.label',
            )}
            placeholder={t(
              'taxInfoDrawer.question2.foreignNonTaxResidency.reasonOther.placeholder',
            )}
            error={!!blockErrors?.noTaxResidencyReasonOther}
            helperText={
              blockErrors?.noTaxResidencyReasonOther?.message ??
              t(
                'taxInfoDrawer.question2.foreignNonTaxResidency.reasonOther.charactersLeft',
                { count: otherReasonCharactersLeft },
              )
            }
            disabled={isPending}
            fullWidth
            inputProps={{
              maxLength: FOREIGN_NON_TAX_RESIDENCY_REASON_OTHER_MAX_LENGTH,
            }}
            {...getDataTestId(
              `tax-info-foreign-non-tax-residency-reason-other-${index}`,
            )}
          />
        )}
      </TaxInfoResidenceBlock>
    </TaxInfoQuestionSection>
  );
}
